'use client';

import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useLocale } from 'next-intl';
import { useLatest } from 'react-use';

import { MAX_MESSAGE_LENGTH } from '@/common/constants';
import { BOOT_RESTORE_WELCOME_TIMEOUT_MS } from '@/hooks/use-boot-restore-welcome-gate';
import { useTrackEvent } from '@/hooks/use-track-event';
import { CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { WINDOW_EVENTS } from '@/lib/constants/widget-events';
import { earlyReplayWait } from '@/lib/early-input/handoff';
import type { EarlyInput } from '@/lib/early-input/types';
import { recordStarterClick } from '@/lib/starter-click-tracking';

type SetComposerText = (text: string, options?: { fromUserInput?: boolean }) => void;

interface EarlyInputHandoff {
  /** The text the box must show in place of assistant-ui's: while a saved conversation opens, and
   *  for a moment after seeding. */
  heldText: string | undefined;
  /** `setComposerText` for the box's own typing and paste handlers: it also records the edit. */
  setVisitorText: SetComposerText;
}

interface Params {
  inputRef: RefObject<HTMLTextAreaElement | null>;
  /** Sets the composer text the way typing does, so it also replaces the saved draft. */
  setComposerText: SetComposerText;
  /** The composer runtime's current text. */
  composerText: string;
  /** The text kept for this chat's composer (its draft): current at once, unlike `composerText`. */
  draftText: string;
  /** Whether a resumed conversation's history has loaded; sends wait for it like auto-sends do. */
  historySettled: () => boolean;
  /** The current thread's server id, once it has one. */
  remoteId: string | undefined;
  /** The saved conversation this load reopens (`readBootThreadId`), or null for a fresh chat. */
  bootThreadId: string | null;
  /** Shows the composer's too-long notice, as typing past the limit does. */
  onTextTooLong: () => void;
}

function seedComposer({
  early,
  input,
  setComposerText,
  onTextTooLong,
}: {
  early: EarlyInput;
  input: HTMLTextAreaElement | null;
  setComposerText: (text: string) => void;
  onTextTooLong: () => void;
}): boolean {
  const text = early.action?.type === 'send' ? early.action.text : early.text;
  // Empty is still the visitor's text: they cleared the box, which replaces an older draft too.
  if (text === null) return false;
  // The server-rendered box has no length limit; the composer's own input handlers enforce it.
  const fitting = text.slice(0, MAX_MESSAGE_LENGTH);
  // Where the visitor left the caret or selection, moved or typed: setting the text moves it.
  const box = input && document.activeElement === input ? input : null;
  const selection =
    box && ([box.selectionStart, box.selectionEnd, box.selectionDirection] as const);
  setComposerText(fitting);
  if (fitting !== text) {
    onTextTooLong();
    // A cut-off message isn't what the visitor meant to send; they send the shortened one.
    if (early.action?.type === 'send') early.action = null;
  }
  if (box && selection) {
    const [start, end, direction] = selection;
    // Typing or pasting in that frame already put the caret where it belongs, even when it left the
    // text unchanged (moving it back put the next keystrokes before the ones just typed).
    let edited = false;
    const markEdited = () => {
      edited = true;
    };
    box.addEventListener('input', markEdited);
    box.addEventListener('paste', markEdited);
    // Setting a box's value moves its caret to the end, so only a caret found there is put back;
    // anywhere else the visitor moved it themselves.
    requestAnimationFrame(function restoreSelection() {
      box.removeEventListener('input', markEdited);
      box.removeEventListener('paste', markEdited);
      const atEnd = box.selectionStart === fitting.length && box.selectionEnd === fitting.length;
      if (!edited && atEnd && box.value === fitting) box.setSelectionRange(start, end, direction);
    });
  }
  return true;
}

/**
 * Takes over what the visitor did before the chat was interactive (see lib/early-input) and
 * replays it through the normal paths: a queued message is submitted through the composer form, so
 * the personal-data and language checks run as for any send; a tapped starter is clicked again,
 * so its analytics and send behave exactly like a live tap.
 *
 * A load that reopens a saved conversation starts on a blank thread and switches to the saved one
 * a moment later. Everything waits for that switch: sent earlier, a message lands in the blank
 * thread the visitor never sees, and seeded text is swapped out for the saved thread's draft. The
 * box keeps showing the visitor's text meanwhile, and their edits are what is seeded; editing a
 * queued message cancels it. Closing the widget cancels whatever waits, as Cancel does.
 *
 * Edits reach it through `setVisitorText`, from the box's typing and paste handlers.
 */
export function useEarlyInputHandoff({
  inputRef,
  setComposerText,
  composerText,
  draftText,
  historySettled,
  remoteId,
  bootThreadId,
  onTextTooLong,
}: Params): EarlyInputHandoff {
  const trackEvent = useTrackEvent();
  const locale = useLocale();
  // Cleared once fully handed over, so every effect below is a no-op from then on.
  const earlyRef = useRef<EarlyInput | null>(null);
  // Set once the composer has shown the queued message, so a later difference is the visitor's edit.
  const queuedTextShownRef = useRef(false);
  // What the box shows while a saved conversation opens, kept up to date with the visitor's edits.
  const [waitingText, setWaitingText] = useState<string | null>(null);
  // Set once that first restore has opened or been given up on: later thread changes are ordinary.
  const restoreSettledRef = useRef(!bootThreadId);
  const [seededTextHold, setSeededTextHold] = useState<'not-yet' | 'holding' | 'done'>('not-yet');
  // WORKAROUND: assistant-ui's composer state starts following the runtime only once its own effects
  // have subscribed, a few frames after the chat mounts; until then every render of the box puts its
  // initial empty text in it. Typing in that moment replaced everything typed while the chat loaded
  // with the new keystroke. The draft is current at once, so the box shows it until the composer
  // state has caught up with it (not merely has text: an older draft may have reached it first).
  // Only the first seed: later ones find the composer state following. Drop this hold once a
  // `setText` before those effects shows on the next render (the rendered handoff test covers it).
  if (seededTextHold === 'holding' && composerText === draftText) setSeededTextHold('done');
  const threadReady = !bootThreadId || remoteId === bootThreadId;

  useLayoutEffect(
    function takeOver() {
      const early = window.__chatEarlyInput?.take();
      if (!early) return;
      earlyRef.current = early;
      const text = early.action?.type === 'send' ? early.action.text : early.text;
      if (text !== null && bootThreadId) setWaitingText(text);
      if (early.cancelledCount > 0) {
        trackEvent({
          event: CHAT_ANALYTICS_EVENTS.EARLY_INPUT_CANCELLED,
          properties: { count: early.cancelledCount },
        });
      }
      if (early.failedShown) {
        trackEvent({
          event: CHAT_ANALYTICS_EVENTS.CHAT_LOAD_FAILED_SHOWN,
          properties: { recovered: true },
        });
      }
    },
    [trackEvent, bootThreadId],
  );

  const waitingForThread = useLatest(!threadReady);
  // Edits are taken where they're made, before their render: the box is controlled by the held text
  // meanwhile, so by the time an effect could look, that render has put the old text back. A saved
  // draft that loads later (the persisted store starts from an empty snapshot) doesn't come this
  // way, so early text wins over it.
  const setVisitorText = useCallback<SetComposerText>(
    function setVisitorText(text, options) {
      // Also typing that starts once the chat is interactive, with nothing from before it.
      if (waitingForThread.current && !restoreSettledRef.current) {
        const early = (earlyRef.current ??= {
          action: null,
          text: null,
          cancelledCount: 0,
          failedShown: false,
        });
        early.text = text;
        if (early.action?.type === 'send' && text !== early.action.text) early.action = null;
        setWaitingText(text);
      }
      setComposerText(text, options);
    },
    [setComposerText, waitingForThread],
  );

  useEffect(
    function cancelWhenWidgetCloses() {
      function onClosed() {
        const early = earlyRef.current;
        if (!early?.action) return;
        early.action = null;
        trackEvent({
          event: CHAT_ANALYTICS_EVENTS.EARLY_INPUT_CANCELLED,
          properties: { count: 1 },
        });
      }
      window.addEventListener(WINDOW_EVENTS.CLOSED, onClosed);
      return () => window.removeEventListener(WINDOW_EVENTS.CLOSED, onClosed);
    },
    [trackEvent],
  );

  // Layout effect: on a fresh chat this puts the visitor's text back before the browser paints the
  // box React has just emptied.
  useLayoutEffect(
    function seedWhenThreadReady() {
      const early = earlyRef.current;
      if (!threadReady) return;
      restoreSettledRef.current = true;
      if (!early) return;
      const seeded = seedComposer({
        early,
        input: inputRef.current,
        setComposerText,
        onTextTooLong,
      });
      if (seeded) setSeededTextHold(hold => (hold === 'not-yet' ? 'holding' : hold));
      setWaitingText(null);
      if (!early.action) earlyRef.current = null;
    },
    [threadReady, inputRef, setComposerText, onTextTooLong],
  );

  useEffect(
    function giveUpOnSlowRestore() {
      if (threadReady || restoreSettledRef.current) return;
      const timer = setTimeout(function returnTextUnsent() {
        restoreSettledRef.current = true;
        setWaitingText(null);
        const early = earlyRef.current;
        if (!early) return;
        // Nothing is sent into whichever thread is open instead: the visitor gets their text back to
        // send themselves (their own typing over a starter's question, which they can tap again).
        // If the conversation still opens later, it shows its own draft, as after any slow restore;
        // this text stays in the new-chat draft.
        earlyRef.current = null;
        setWaitingText(null);
        const { action } = early;
        const text =
          action?.type === 'send'
            ? action.text
            : early.text || (action?.type === 'starter' ? action.title : null);
        seedComposer({
          early: { ...early, action: null, text },
          input: inputRef.current,
          setComposerText,
          onTextTooLong,
        });
      }, BOOT_RESTORE_WELCOME_TIMEOUT_MS);
      return () => clearTimeout(timer);
    },
    [threadReady, inputRef, setComposerText, onTextTooLong],
  );

  useEffect(
    function replayWhenSettled() {
      const early = earlyRef.current;
      const action = early?.action;
      if (!early || !action || !threadReady) return;

      if (action.type === 'starter') {
        if (!historySettled()) return;
        const button = document.querySelector<HTMLElement>(
          `[data-starter-id="${CSS.escape(action.starterId)}"]`,
        );
        if (!button) {
          // The starters are gone (a saved conversation opened, or they changed once the chat
          // loaded): the question tapped goes as a message, over text typed before the tap, as a tap
          // does on a loaded chat, and is recorded as the tap it was.
          recordStarterClick({
            trackEvent,
            starterId: action.starterId,
            text: action.title,
            position: action.position,
            isDated: action.isDated ?? false,
            isPinned: action.isPinned ?? false,
            isRotating: action.isRotating ?? false,
            audience: action.audience,
            language: locale,
          });
          early.action = { type: 'send', text: action.title, at: action.at };
          setComposerText(action.title);
          return;
        }
        earlyRef.current = null;
        earlyReplayWait.set({ text: action.title, value: Date.now() - action.at });
        button.click();
        return;
      }

      // Wait until the runtime holds the text, so the form submits what the visitor queued. If they
      // edit it before it goes, they changed their mind: the queued send is dropped.
      if (composerText !== action.text) {
        if (queuedTextShownRef.current) earlyRef.current = null;
        return;
      }
      queuedTextShownRef.current = true;
      if (!historySettled()) return;
      earlyRef.current = null;
      earlyReplayWait.set({ text: action.text, value: Date.now() - action.at });
      const form = inputRef.current?.form;
      // Safari before 16 has no requestSubmit.
      if (typeof form?.requestSubmit === 'function') form.requestSubmit();
      else form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    [threadReady, composerText, historySettled, inputRef, setComposerText, trackEvent, locale],
  );

  const heldText =
    waitingText ??
    (seededTextHold === 'holding' && composerText !== draftText ? draftText : undefined);
  return { heldText, setVisitorText };
}
